<template>
  <div id="app" class="min-h-screen bg-gray-900 text-white">
    <!-- Navigation Header -->
    <Navbar v-if="!isFullscreenRoute" />
    
    <!-- Main Content -->
    <main :class="{ 'pt-20': !isFullscreenRoute }">
      <RouterView />
    </main>
    
    <!-- Footer -->
    <Footer v-if="!isFullscreenRoute" />
    
    <!-- Global Loading Overlay -->
    <LoadingOverlay v-if="isLoading" />
    
    <!-- Toast Notifications -->
    <ToastContainer />
    
    <!-- Web3 Connection Modal -->
    <Web3Modal 
      v-if="showWeb3Modal" 
      @close="showWeb3Modal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useWeb3Store } from '@/stores/web3'
import { useGameStore } from '@/stores/game'
import { useToastStore } from '@/stores/toast'
import Navbar from '@/components/layout/Navbar.vue'
import Footer from '@/components/layout/Footer.vue'
import LoadingOverlay from '@/components/ui/LoadingOverlay.vue'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import Web3Modal from '@/components/web3/Web3Modal.vue'

const route = useRoute()
const web3Store = useWeb3Store()
const gameStore = useGameStore()
const toastStore = useToastStore()

const isFullscreenRoute = computed(() => {
  return route.meta?.fullscreen === true
})

const isLoading = computed(() => {
  return web3Store.isConnecting || gameStore.isLoading
})

const showWeb3Modal = computed(() => {
  return web3Store.showConnectionModal
})

// Initialize app
onMounted(async () => {
  try {
    // Try to restore previous wallet connection
    await web3Store.tryAutoConnect()
    
    // Initialize game data if connected
    if (web3Store.isConnected) {
      await gameStore.initialize()
    }
  } catch (error) {
    console.error('Failed to initialize app:', error)
    toastStore.addToast({
      type: 'error',
      title: 'Initialization Error',
      message: 'Failed to initialize the application. Please refresh and try again.'
    })
  }
})

// Watch for web3 connection changes
watch(
  () => web3Store.isConnected,
  async (isConnected) => {
    if (isConnected) {
      await gameStore.initialize()
    } else {
      gameStore.reset()
    }
  }
)

// Handle account changes
watch(
  () => web3Store.address,
  async (newAddress, oldAddress) => {
    if (newAddress && newAddress !== oldAddress) {
      await gameStore.loadUserData(newAddress)
    }
  }
)
</script>

<style>
/* Global styles */
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  @apply bg-gray-800;
}

::-webkit-scrollbar-thumb {
  @apply bg-gray-600 rounded;
}

::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-500;
}
</style>